import {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { IconButton, Box, Button, Typography } from "@mui/material";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { defaultText, IEmoji, IText } from "./subirHistorias";
import { styled } from "@mui/system";

const StoryWidget = styled(IconButton)(({ theme }) => ({
  color: "white",
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  borderRadius: "50%",
  padding: "5px",
}));

const StoryEditor = forwardRef(
  (
    {
      image,
      setImage,
      imgDimensions,
      setImgDimensions,
    }: {
      image: File;
      setImage: (image: File | null) => void;
      imgDimensions: { width: number; height: number };
      setImgDimensions: (dim: { width: number; height: number }) => void;
    },
    ref
  ) => {
    const [crop, setCrop] = useState<Crop>({
      unit: "%",
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    });
    const [src, setSrc] = useState<string | null>(null);
    const [croppedImage, setCroppedImage] = useState<string | null>(null);
    const [rotation, setRotation] = useState<number>(0);
    const imageRef = useRef<HTMLImageElement>(null);

    const handleImageLoad = () => {
      if (imageRef.current) {
        const newWidth = imageRef.current.width;
        const newHeight = imageRef.current.height;

        // Actualiza las dimensiones solo si han cambiado
        if (
          newWidth !== imgDimensions.width ||
          newHeight !== imgDimensions.height
        ) {
          setImgDimensions({
            width: newWidth,
            height: newHeight,
          });
        }
      }
    };

    useEffect(() => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSrc(reader.result as string);
      };
      if (image) {
        reader.readAsDataURL(image);
      }
    }, [image]);

    const getRotatedImage = (
      img: HTMLImageElement,
      degree: number
    ): Promise<Blob | null> => {
      return new Promise((resolve) => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const radians = (degree * Math.PI) / 180;
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        const newWidth =
          Math.abs(Math.cos(radians)) * width +
          Math.abs(Math.sin(radians)) * height;
        const newHeight =
          Math.abs(Math.sin(radians)) * width +
          Math.abs(Math.cos(radians)) * height;

        canvas.width = newWidth;
        canvas.height = newHeight;

        if (ctx) {
          ctx.translate(newWidth / 2, newHeight / 2);
          ctx.rotate(radians);
          ctx.drawImage(img, -width / 2, -height / 2);
        }

        canvas.toBlob((blob) => {
          resolve(blob);
        });
      });
    };

    const getCroppedImg = async (): Promise<File | undefined> => {
      if (!imageRef.current) return;

      const isFullCrop =
        crop.x === 0 &&
        crop.y === 0 &&
        crop.width === 100 &&
        crop.height === 100;

      if (isFullCrop) {
        return image;
      }

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
      const scaleY = imageRef.current.naturalHeight / imageRef.current.height;
      canvas.width = crop.width;
      canvas.height = crop.height;

      if (!ctx) return;

      ctx.drawImage(
        imageRef.current,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );

      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            const croppedFile = new File([blob], image.name, {
              type: image.type,
            });
            setCroppedImage(URL.createObjectURL(croppedFile));
            setImage(croppedFile);
            resolve(croppedFile);
          } else {
            resolve(undefined);
          }
        });
      });
    };

    const handleRotate = async () => {
      if (!imageRef.current || !image) return;

      const rotatedBlob = await getRotatedImage(imageRef.current, 90); // rota solo una vez

      if (rotatedBlob) {
        const rotatedFile = new File([rotatedBlob], image.name, {
          type: image.type,
        });

        setImage(rotatedFile);
      }
    };

    useImperativeHandle(ref, () => ({
      getCroppedImg,
    }));

    return (
      <>
        <Box
          sx={{
            width: "100%",
            top: 0,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            position: "absolute",
            backgroundColor: "#4D4D4D",
            color: "white",
          }}
        >
          <IconButton
            sx={{
              color: "white",
            }}
            onClick={() => {
              if (image) setImage(null);
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            style={{
              marginTop: "10px",
              marginBottom: "10px",
              fontWeight: "bold",
              fontSize: "16px",
            }}
          >
            Recortá la foto
          </Typography>
          <div>
            <IconButton sx={{ color: "white" }} onClick={handleRotate}>
              <SettingsBackupRestoreIcon />
            </IconButton>
          </div>
        </Box>
        {src && (
          <ReactCrop
            crop={crop}
            onChange={(newCrop) => setCrop(newCrop)}
            onComplete={(c) => setCrop(c)}
            style={{
              maxHeight: "calc(100vh - 100px)",
              overflow: "auto",
            }}
          >
            <img
              ref={imageRef}
              src={src}
              alt="Crop"
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100vh - 150px)",
                transform: `rotate(${rotation}deg)`,
                objectFit: "contain",
                display: "block",
                margin: "0 auto",
              }}
              onLoad={handleImageLoad}
            />
          </ReactCrop>
        )}
      </>
    );
  }
);

export default StoryEditor;
